import { AmountDialog, Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, ConfirmDialog, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, EmptyState, Input, Label, MoneyInput, PageHeader, StatusBadge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Tabs, TabsContent, TabsList, TabsTrigger, lotStatusVariant, saleStatusVariant, toast } from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import type { SaleDetail, SaleLot, ServerEvent } from '@bia-auction/shared-schema';
import {
  bidAdminViewSchema,
  depositAdminViewSchema,
  saleDetailSchema,
} from '@bia-auction/shared-schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Link, useParams } from '@tanstack/react-router';
import { AlertTriangle, Download, Inbox, MoreHorizontal, ScrollText, SquarePen, Timer } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorMsg, Loading } from '../components/Feedback';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { currencySymbol, formatCountdown, formatDateTime, formatKurus } from '../lib/format';
import { bidKindLabel, depositStateLabel, lotStateLabel, presentationLabel } from '../lib/labels';
import { can } from '../lib/roles';
import { useSaleRealtime } from '../lib/ws';

/** Live overlay fields per lot, merged over the fetched detail from WS events. */
type LotOverlay = Partial<
  Pick<SaleLot, 'currentTopBid' | 'bidCount' | 'state' | 'endDate' | 'reserveCrossed'>
>;

/** Per-action permissions for a lot's controls (each gated to the roles the API accepts). */
interface LotPerms {
  bid: boolean; // adminBid — +1 / jump
  freeBid: boolean; // adminFreeBid
  lifecycle: boolean; // salesLifecycle — reserve / take-control / hammer
  remove: boolean; // salesCreate
  reAdd: boolean; // reAdd
}

export function SaleDetailPage() {
  const { saleId } = useParams({ strict: false }) as { saleId: string };
  const { state: auth } = useAuth();
  const qc = useQueryClient();

  const perms: LotPerms = {
    bid: can(auth.roles, 'adminBid'),
    freeBid: can(auth.roles, 'adminFreeBid'),
    lifecycle: can(auth.roles, 'salesLifecycle'),
    remove: can(auth.roles, 'salesCreate'),
    reAdd: can(auth.roles, 'reAdd'),
  };
  const canPauseResume = perms.lifecycle;

  const detail = useQuery({
    queryKey: ['sale', saleId],
    queryFn: () => api(`/admin/sales/${saleId}`, {}, saleDetailSchema),
  });
  const deposits = useQuery({
    queryKey: ['sale', saleId, 'deposits'],
    queryFn: () => api(`/admin/sales/${saleId}/deposits`, {}, z.array(depositAdminViewSchema)),
    enabled: can(auth.roles, 'depositView'),
  });
  const bids = useQuery({
    queryKey: ['sale', saleId, 'bids'],
    queryFn: () => api(`/admin/sales/${saleId}/bids`, {}, z.array(bidAdminViewSchema)),
  });

  const [overlays, setOverlays] = useState<Record<string, LotOverlay>>({});
  const [editOpen, setEditOpen] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), 1_000);
    return () => clearInterval(timer);
  }, []);

  const lotRooms = useMemo(
    () => (detail.data?.lots ?? []).map((l) => `lot:${l.id}`),
    [detail.data],
  );

  const onEvent = useCallback(
    (e: ServerEvent) => {
      switch (e.type) {
        case 'bid.placed':
          setOverlays((o) => ({
            ...o,
            [e.lotId]: {
              ...o[e.lotId],
              // greenLight (set-reserve) emits a synthetic bid.placed with bidCount:0 to refresh
              // priceGoalReached — that isn't a real bid, so don't overlay a current top bid for
              // it (it would falsely show `startingPrice` on a never-bid lot).
              ...(e.bidCount > 0 ? { currentTopBid: e.amount, bidCount: e.bidCount } : {}),
              // A bid/reserve change that reaches the price goal crosses the reserve → reflect
              // the guaranteed state immediately instead of waiting for a detail refetch.
              ...(e.priceGoalReached ? { reserveCrossed: true } : {}),
            },
          }));
          void qc.invalidateQueries({ queryKey: ['sale', saleId, 'bids'] });
          break;
        case 'lot.opened':
          setOverlays((o) => ({ ...o, [e.lotId]: { ...o[e.lotId], state: 'OPEN', endDate: e.endsAt } }));
          // Opening the first lot also promotes the sale PUBLISHED→LIVE (no separate event), so
          // refetch the detail to pick up the new sale.state (gates pause + bid controls).
          void qc.invalidateQueries({ queryKey: ['sale', saleId] });
          break;
        case 'lot.extended':
          setOverlays((o) => ({ ...o, [e.lotId]: { ...o[e.lotId], endDate: e.newEndsAt } }));
          break;
        case 'lot.closed':
          setOverlays((o) => ({ ...o, [e.lotId]: { ...o[e.lotId], state: e.finalState } }));
          break;
        case 'lot.removed':
          // Mark the removed lot REMOVED (so its card stops showing a countdown/actions) AND
          // apply the reschedule the event carries to the pulled-earlier lots, so their overlaid
          // deadlines don't stay stale after the refetch.
          setOverlays((o) => {
            const next: Record<string, LotOverlay> = { ...o, [e.lotId]: { state: 'REMOVED' } };
            for (const r of e.reschedule) next[r.lotId] = { ...next[r.lotId], endDate: r.newEndDate };
            return next;
          });
          void qc.invalidateQueries({ queryKey: ['sale', saleId] });
          break;
        case 'conference.available':
          toast(t('saleDetail.confAvailable'), 'success');
          // The room may have been provisioned by the fallback after the initial fetch (which
          // then had conference: null) — refetch so the join/host link actually appears.
          void qc.invalidateQueries({ queryKey: ['sale', saleId] });
          break;
        case 'sale.resumed':
          // Apply the recomputed schedule to overlays so a resumed lot's countdown uses the
          // new deadline immediately, instead of the stale pre-pause endDate, while the detail
          // refetches.
          setOverlays((o) => {
            const next = { ...o };
            for (const r of e.reschedule) next[r.lotId] = { ...next[r.lotId], endDate: r.newEndDate };
            return next;
          });
          void qc.invalidateQueries({ queryKey: ['sale', saleId] });
          break;
        case 'sale.paused':
        case 'sale.closed':
          void qc.invalidateQueries({ queryKey: ['sale', saleId] });
          break;
      }
    },
    [qc, saleId],
  );

  const onReconnect = useCallback(() => {
    // Missed events aren't replayed — drop overlays and refetch a fresh snapshot on reconnect.
    setOverlays({});
    void qc.invalidateQueries({ queryKey: ['sale', saleId] });
  }, [qc, saleId]);

  useSaleRealtime(saleId, lotRooms, onEvent, onReconnect);

  /**
   * The idempotency key for the console bid in progress (M19). Same rule as the bidder app:
   * a key is **kept after a failure**, because a bid the server committed but whose response
   * was lost leaves the operator pressing again, and **cleared after a success**, where the
   * next press is a deliberate second bid. Scoped by the request so retrying a jump at a
   * different target is treated as the new intent it is.
   *
   * Only the bid endpoints get one. The lifecycle actions (pause, take-control, close,
   * set-reserve, remove) are already idempotent by state check — a repeat lands on a lot that
   * has left the state it required and is refused, which is the correct answer.
   */
  /**
   * Idempotency keys for console bids, **one per distinct request** (M19).
   *
   * A map rather than a single slot: one mutation is shared by every lot's controls, so in a
   * parallel auction bids on different lots overlap. A single slot loses the first bid's key
   * the moment the second starts — and if that first bid then commits with its response lost,
   * the operator's retry mints a fresh key and commits a duplicate.
   *
   * Keyed by the request itself so a retry at a different jump target is treated as the new
   * intent it is. An entry is kept after a failure (that press is the retry) and dropped after
   * a success (that press is a second bid).
   */
  const bidAttempts = useRef(new Map<string, string>());

  const act = useMutation({
    mutationFn: async ({ path, body }: { path: string; body?: unknown }) => {
      if (!path.includes('/bids')) return api(path, { method: 'POST', body });
      const signature = `${path}:${JSON.stringify(body ?? {})}`;
      let key = bidAttempts.current.get(signature);
      if (!key) {
        key = crypto.randomUUID();
        bidAttempts.current.set(signature, key);
      }
      await api(path, { method: 'POST', body, idempotencyKey: key });
      bidAttempts.current.delete(signature);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sale', saleId] });
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : t('saleDetail.actionFailed'), 'error'),
  });

  const onExport = async () => {
    try {
      const [d, dep, b] = await Promise.all([
        api(`/admin/sales/${saleId}`, {}, saleDetailSchema),
        can(auth.roles, 'depositView')
          ? api(`/admin/sales/${saleId}/deposits`, {}, z.array(depositAdminViewSchema))
          : Promise.resolve([]),
        api(`/admin/sales/${saleId}/bids`, {}, z.array(bidAdminViewSchema)),
      ]);
      // Lazy-load SheetJS so it stays out of the initial bundle (export is a rare action).
      const { exportSaleXlsx } = await import('../lib/xlsx');
      exportSaleXlsx(d, dep, b);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('saleDetail.exportFailed'), 'error');
    }
  };

  if (detail.isLoading) return <Loading />;
  if (detail.isError) return <ErrorMsg error={detail.error} />;
  const sale = detail.data!;

  const lotView = (l: SaleLot): SaleLot => ({ ...l, ...overlays[l.id] });
  // reAddLot accepts LIVE, or PAUSED for non-parallel styles (a paused parallel sale 409s).
  const saleReAddable =
    sale.state === 'LIVE' || (sale.state === 'PAUSED' && sale.style !== 'parallel');

  const editable =
    can(auth.roles, 'salesEdit') && sale.state === 'PUBLISHED' && sale.listingDate > Date.now();
  const auditable = can(auth.roles, 'auditView');
  const exportable = can(auth.roles, 'exportSale');

  return (
    <div className="space-y-4">
      <PageHeader
        title={sale.title ?? sale.code}
        subline={
          <span className="flex items-center gap-2">
            {sale.location}
            <StatusBadge variant={saleStatusVariant(sale.state)}>
              {presentationLabel(sale.presentationStatus)}
            </StatusBadge>
          </span>
        }
      >
        {canPauseResume && sale.state === 'LIVE' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => act.mutate({ path: `/admin/sales/${saleId}/pause` })}
          >
            {t('saleDetail.pause')}
          </Button>
        )}
        {canPauseResume && sale.state === 'PAUSED' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => act.mutate({ path: `/admin/sales/${saleId}/resume` })}
          >
            {t('saleDetail.resume')}
          </Button>
        )}
        {(auditable || editable || exportable) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={t('common.actions')}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {auditable && (
                <DropdownMenuItem asChild>
                  {/* Audit deep-link (M10): the viewer pre-filtered to this sale's trail. */}
                  <Link to="/audit" search={{ entityType: 'sale', entityId: saleId }}>
                    <ScrollText className="h-4 w-4" /> {t('saleDetail.viewAudit')}
                  </Link>
                </DropdownMenuItem>
              )}
              {exportable && (
                <DropdownMenuItem onSelect={() => void onExport()}>
                  <Download className="h-4 w-4" /> {t('saleDetail.export')}
                </DropdownMenuItem>
              )}
              {editable && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setEditOpen(true)}>
                    <SquarePen className="h-4 w-4" /> {t('saleDetail.edit')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </PageHeader>
      {editable && <SaleEditDialog sale={sale} open={editOpen} onOpenChange={setEditOpen} />}

      {sale.conference?.joinUrl && (
        <a
          href={sale.conference.hostUrl ?? sale.conference.joinUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-block text-sm text-tx-brand hover:underline"
        >
          {t('saleDetail.joinJointConf')}
        </a>
      )}

      {/* "Kontrolü al bırak neye yarıyor" — the button label names the toggle but not the two
          behaviours it switches on. Once above the grid rather than on every card: a parallel
          sale opens every lot at once, and the same three lines repeated per card is noise the
          operator learns to skip. The button keeps the same text as its `title`. */}
      {perms.lifecycle && sale.lots.length > 0 && (
        <p className="text-xs text-muted-foreground">{t('saleDetail.control.help')}</p>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {sale.lots.map((raw) => (
          <LotCard
            key={raw.id}
            saleId={saleId}
            lot={lotView(raw)}
            saleLive={sale.state === 'LIVE'}
            saleReAddable={saleReAddable}
            perms={perms}
            onAction={(path, body) => act.mutate({ path, body })}
          />
        ))}
      </div>

      {/* Deposits + bid history live in tabs below the grid (redesign-admin.md §5) so the
          operator keeps the live lots on screen instead of scrolling past two tables. */}
      <Tabs defaultValue={can(auth.roles, 'depositView') ? 'deposits' : 'bids'}>
        <TabsList>
          {can(auth.roles, 'depositView') && (
            <TabsTrigger value="deposits">
              {t('saleDetail.deposits')} ({deposits.data?.length ?? 0})
            </TabsTrigger>
          )}
          <TabsTrigger value="bids">
            {t('saleDetail.bidHistory')} ({bids.data?.length ?? 0})
          </TabsTrigger>
        </TabsList>
        {can(auth.roles, 'depositView') && (
          <TabsContent value="deposits">
            {deposits.isLoading ? (
              <Loading />
            ) : deposits.isError ? (
              <ErrorMsg error={deposits.error} />
            ) : (deposits.data?.length ?? 0) === 0 ? (
              <EmptyState icon={Inbox} title={t('saleDetail.noDeposits')} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('saleDetail.col.participant')}</TableHead>
                    <TableHead>{t('saleDetail.col.plate')}</TableHead>
                    <TableHead>{t('saleDetail.col.amount')}</TableHead>
                    <TableHead>{t('saleDetail.col.status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(deposits.data ?? []).map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>{d.bidderName ?? d.bidderEmail ?? t('common.none')}</TableCell>
                      <TableCell>{d.plateSnapshot ?? t('common.none')}</TableCell>
                      <TableCell className="tabular-nums">{formatKurus(d.amount)}</TableCell>
                      <TableCell>
                        <StatusBadge variant={d.state === 'HELD' ? 'success' : 'secondary'}>
                          {depositStateLabel(d.state)}
                        </StatusBadge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>
        )}
        <TabsContent value="bids">
          {bids.isLoading ? (
            <Loading />
          ) : bids.isError ? (
            <ErrorMsg error={bids.error} />
          ) : (bids.data?.length ?? 0) === 0 ? (
            <EmptyState icon={Inbox} title={t('saleDetail.noBids')} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('saleDetail.col.order')}</TableHead>
                  <TableHead>{t('saleDetail.col.plate')}</TableHead>
                  <TableHead>{t('saleDetail.col.participant')}</TableHead>
                  <TableHead>{t('saleDetail.col.amount')}</TableHead>
                  <TableHead>{t('saleDetail.col.kind')}</TableHead>
                  <TableHead>{t('saleDetail.col.time')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(bids.data ?? []).slice(0, 100).map((b) => (
                  <TableRow key={b.id}>
                    <TableCell className="tabular-nums">{b.orderIndex + 1}</TableCell>
                    <TableCell>{b.plateSnapshot ?? t('common.none')}</TableCell>
                    <TableCell>{b.bidderName ?? t('common.none')}</TableCell>
                    <TableCell className="tabular-nums">{formatKurus(b.amount, b.currency)}</TableCell>
                    <TableCell>{bidKindLabel(b.kind)}</TableCell>
                    <TableCell className="tabular-nums">{formatDateTime(b.placedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/** Pre-live sale edit (title/location/default reserve/company gating) → PATCH /admin/sales/:id.
 * Controlled — opened from the header's overflow menu. */
function SaleEditDialog({
  sale,
  open,
  onOpenChange,
}: {
  sale: SaleDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(sale.title ?? '');
  const [location, setLocation] = useState(sale.location ?? '');
  const [defaultReserve, setDefaultReserve] = useState<number | null>(sale.defaultReserve ?? null);

  const save = useMutation({
    mutationFn: () =>
      api(
        `/admin/sales/${sale.id}`,
        {
          method: 'PATCH',
          // targetCompany is intentionally NOT editable post-create (scope changes are
          // recreate-only — ADR 0019); only display fields here.
          body: {
            title: title || null,
            location: location || null,
            defaultReserve,
          },
        },
        saleDetailSchema,
      ),
    onSuccess: () => {
      toast(t('saleDetail.updated'), 'success');
      onOpenChange(false);
      void qc.invalidateQueries({ queryKey: ['sale', sale.id] });
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : t('saleDetail.editFailed'), 'error'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('saleDetail.editTitle')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="se-title">{t('newSale.titleField')}</Label>
            <Input id="se-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="se-location">{t('newSale.location')}</Label>
            <Input id="se-location" value={location} onChange={(e) => setLocation(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="se-reserve">{t('newSale.defaultReserve')}</Label>
            <MoneyInput id="se-reserve" value={defaultReserve} onValueChange={setDefaultReserve} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LotCard({
  saleId,
  lot,
  saleLive,
  saleReAddable,
  perms,
  onAction,
}: {
  saleId: string;
  lot: SaleLot;
  saleLive: boolean;
  saleReAddable: boolean;
  perms: LotPerms;
  onAction: (path: string, body?: unknown) => void;
}) {
  const base = `/admin/sales/${saleId}/lots/${lot.id}`;
  const bidBase = `/sales/${saleId}/lots/${lot.id}`;
  const isActive = lot.state === 'OPEN' || lot.state === 'SCHEDULED';
  // Bidding only works for an OPEN lot in a LIVE sale (SaleDO.placeBid rejects otherwise); the
  // countdown is only meaningful then too (it freezes while the sale is paused).
  const biddable = saleLive && lot.state === 'OPEN';
  const remaining = biddable ? lot.endDate - Date.now() : 0;
  const closingSoon = biddable && remaining < 30_000;

  const [dialog, setDialog] = useState<null | 'jump' | 'free' | 'reserve' | 'remove' | 'reAdd'>(null);
  const close = () => setDialog(null);
  const currency = currencySymbol(lot.currency);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">
          #{lot.orderIndex + 1} · {lot.plateSnapshot}
        </CardTitle>
        <StatusBadge variant={lotStatusVariant(lot.state)}>
          {lotStateLabel(lot.state)}
        </StatusBadge>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {/* Operator-critical readout (redesign-admin.md §5): price in display type, then a
            single meta row. The countdown escalates to warning (color + icon + text) under
            30s — never color alone. */}
        <div className="font-head text-display font-semibold tabular-nums">
          {formatKurus(lot.currentTopBid, lot.currency)}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
          <span className="tabular-nums">{t('saleDetail.bidCountShort', { n: String(lot.bidCount) })}</span>
          {biddable && (
            <span
              className={
                closingSoon
                  ? 'flex items-center gap-1 font-medium text-warning'
                  : 'flex items-center gap-1'
              }
            >
              <Timer aria-hidden className="h-4 w-4" />
              <span className="font-mono tabular-nums">
                {t('saleDetail.remainingShort', { time: formatCountdown(remaining) })}
              </span>
            </span>
          )}
          {(lot.reserveCrossed || lot.saleMode === 'guaranteed') && (
            <StatusBadge variant="success">{t('saleDetail.guaranteed')}</StatusBadge>
          )}
          {/* Take-control is a mode, not a momentary action — without a state label the only
              clue it is on was the button flipping to "Kontrolü bırak". */}
          {lot.manualControl && (
            <StatusBadge variant="warning">{t('saleDetail.control.on')}</StatusBadge>
          )}
        </div>
        {lot.conference?.joinUrl && (
          <a
            href={lot.conference.hostUrl ?? lot.conference.joinUrl}
            target="_blank"
            rel="noreferrer"
            className="block text-tx-link hover:underline"
          >
            {t('saleDetail.joinLotConf')}
          </a>
        )}

        {isActive && (
          <div className="space-y-1.5 pt-2">
            {/* Bid group — "+1" is the card's one filled action (docs/15 §8). */}
            {(perms.bid || perms.freeBid) && biddable && (
              <div className="flex flex-wrap gap-1.5">
                {perms.bid && (
                  <>
                    <Button size="sm" onClick={() => onAction(`${bidBase}/bids`)}>
                      {t('saleDetail.action.bid')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setDialog('jump')}>
                      {t('saleDetail.action.jump')}
                    </Button>
                  </>
                )}
                {perms.freeBid && (
                  <Button size="sm" variant="outline" onClick={() => setDialog('free')}>
                    {t('saleDetail.action.free')}
                  </Button>
                )}
              </div>
            )}
            {/* Lifecycle group. */}
            {(perms.lifecycle || perms.remove) && (
              <div className="flex flex-wrap gap-1.5">
                {perms.lifecycle && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setDialog('reserve')}>
                      {t('saleDetail.action.reserve')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      title={t('saleDetail.control.help')}
                      onClick={() =>
                        onAction(`${base}/take-control`, {
                          manualControl: !lot.manualControl,
                          softCloseMs: 30_000,
                        })
                      }
                    >
                      {lot.manualControl
                        ? t('saleDetail.action.releaseControl')
                        : t('saleDetail.action.takeControl')}
                    </Button>
                    {/* Manual hammer needs take-control on AND a LIVE sale (closeLot rejects a
                        paused sale) — only offer it then. */}
                    {saleLive && lot.state === 'OPEN' && lot.manualControl && (
                      <Button size="sm" variant="destructive" onClick={() => onAction(`${base}/close`)}>
                        {t('saleDetail.action.hammer')}
                      </Button>
                    )}
                  </>
                )}
                {perms.remove && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label={t('common.actions')}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem destructive onSelect={() => setDialog('remove')}>
                        {t('saleDetail.action.remove')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            )}
          </div>
        )}

        {perms.reAdd && saleReAddable && lot.state === 'UNSOLD' && (
          <div className="pt-2">
            <Button size="sm" variant="outline" onClick={() => setDialog('reAdd')}>
              {t('saleDetail.action.reAdd')}
            </Button>
          </div>
        )}
      </CardContent>

      {/* Money entry through validated dialogs — prompt()/confirm() are banned (spec §0 D1). */}
      <AmountDialog
        open={dialog === 'jump'}
        onOpenChange={(o) => !o && close()}
        title={t('saleDetail.action.jump')}
        label={t('saleDetail.prompt.jump', { currency })}
        min={(lot.currentTopBid ?? 0) + 1}
        minLabel={t('saleDetail.dialog.mustExceedTop')}
        confirmLabel={t('saleDetail.action.jump')}
        cancelLabel={t('common.cancel')}
        onSubmit={(target) => {
          onAction(`${bidBase}/bids/jump`, { target });
          close();
        }}
      />
      <AmountDialog
        open={dialog === 'free'}
        onOpenChange={(o) => !o && close()}
        title={t('saleDetail.action.free')}
        label={t('saleDetail.prompt.free', { currency })}
        min={1}
        minLabel={t('saleDetail.dialog.invalidAmount')}
        confirmLabel={t('saleDetail.action.free')}
        cancelLabel={t('common.cancel')}
        onSubmit={(bid) => {
          onAction(`${bidBase}/bids/free`, { bid });
          close();
        }}
      />
      <AmountDialog
        open={dialog === 'reserve'}
        onOpenChange={(o) => !o && close()}
        title={t('saleDetail.action.reserve')}
        // Seeded with the lot's current reserve: "Rezerv ne oluyor" was asked in front of an
        // empty box that gave no hint what was already set. The value stays inside the dialog
        // (never on the card face) — the console is screen-shared during the sale conference,
        // and the reserve is hidden from bidders by design (ADR 0013).
        initial={lot.reserve ?? null}
        description={<ReserveHelp lot={lot} />}
        label={t('saleDetail.prompt.reserve', { currency })}
        min={1}
        minLabel={t('saleDetail.dialog.invalidAmount')}
        confirmLabel={t('saleDetail.action.reserve')}
        cancelLabel={t('common.cancel')}
        onSubmit={(reserve) => {
          onAction(`${base}/green`, { reserve });
          close();
        }}
      />
      <ConfirmDialog
        open={dialog === 'remove'}
        onOpenChange={(o) => !o && close()}
        title={t('saleDetail.action.remove')}
        description={t('saleDetail.confirm.remove')}
        confirmLabel={t('saleDetail.action.remove')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={() => {
          onAction(`${base}/remove`);
          close();
        }}
      />
      {dialog === 'reAdd' && (
        <ReAddDialog
          currency={lot.currency}
          onOpenChange={(o) => !o && close()}
          onSubmit={(guaranteed, reserve) => {
            onAction(`/admin/sales/${saleId}/lots/re-add`, {
              lotId: lot.id,
              ...(guaranteed ? { saleMode: 'guaranteed' } : {}),
              ...(reserve !== undefined ? { reserve } : {}),
            });
            close();
          }}
        />
      )}
    </Card>
  );
}

/**
 * What the reserve dialog has to say before an operator types into it (Bia feedback,
 * 07.08.2026 — "Rezerv ne oluyor… ikinci kez girip rezerv dememin bir anlamı var mı"):
 *
 * - what the reserve *is* (the hidden floor; crossing it auto-flips the lot to kesin satış);
 * - what is currently set, if anything;
 * - and, once it has been crossed, that re-entering it changes nothing about the outcome.
 *   That last one is the engine's actual behaviour, not a UI guess: `reserve_crossed` is a
 *   latch owned by `placeBid`, and `closeLot` settles SOLD on `reserveCrossed || committed
 *   top bid ≥ committed reserve` — so raising the reserve after a cross cannot un-sell the lot.
 */
function ReserveHelp({ lot }: { lot: SaleLot }) {
  const crossed = lot.reserveCrossed;
  const guaranteed = lot.saleMode === 'guaranteed';
  return (
    <div className="space-y-1.5">
      <p>{t('saleDetail.reserve.help')}</p>
      <p>
        {lot.reserve != null
          ? t('saleDetail.reserve.current', { amount: formatKurus(lot.reserve, lot.currency) })
          : t('saleDetail.reserve.none')}
      </p>
      {guaranteed ? (
        <p>{t('saleDetail.reserve.guaranteedHelp')}</p>
      ) : crossed ? (
        <p className="flex items-start gap-1.5 rounded-md bg-warning-tint px-2 py-1.5 text-warning-tint-fg">
          <AlertTriangle aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <strong>{t('saleDetail.reserve.crossedTitle')}</strong> —{' '}
            {t('saleDetail.reserve.crossedHelp')}
          </span>
        </p>
      ) : (
        <p>{t('saleDetail.reserve.lowerHint')}</p>
      )}
    </div>
  );
}

/** Re-add an UNSOLD lot: guaranteed toggle + optional new reserve in one dialog
 * (blank reserve = inherit the source lot's; guaranteed clears it). */
function ReAddDialog({
  currency,
  onOpenChange,
  onSubmit,
}: {
  currency: string | null | undefined;
  onOpenChange: (open: boolean) => void;
  onSubmit: (guaranteed: boolean, reserve: number | undefined) => void;
}) {
  const [guaranteed, setGuaranteed] = useState(false);
  const [reserve, setReserve] = useState<number | null>(null);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('saleDetail.action.reAdd')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={guaranteed}
              onCheckedChange={(v) => {
                setGuaranteed(v === true);
                if (v === true) setReserve(null);
              }}
            />
            {t('saleDetail.dialog.reAddGuaranteed')}
          </label>
          <div className="space-y-1.5">
            <Label htmlFor="readd-reserve">
              {t('saleDetail.prompt.reAddReserve', { currency: currencySymbol(currency ?? undefined) })}
            </Label>
            <MoneyInput
              id="readd-reserve"
              value={reserve}
              onValueChange={setReserve}
              disabled={guaranteed}
            />
            <div className="text-xs text-muted-foreground">{t('saleDetail.dialog.reAddReserveHint')}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => onSubmit(guaranteed, guaranteed ? undefined : (reserve ?? undefined))}>
            {t('saleDetail.action.reAdd')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
